import { ChangeDetectorRef, Component, Inject } from '@angular/core';
import { FormControl, FormGroup, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import {
  MAT_DIALOG_DATA, MatDialog,
  MatDialogConfig, MatDialogRef
} from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { merge, of } from 'rxjs';
import {
  catchError, debounceTime, finalize, map, switchMap, take, takeUntil
} from 'rxjs/operators';

import {
  AssignFollowUpGQL,
  CreateFollowUpGQL, EntityCoreFragment, EntityWithBasicInfoFragment,
  FollowUpDetailsFragment,
  NoteDetails, NoteDetailsFragment, NoteTarget, SearchUserQueryGQL, UpdateNoteGQL, UserPublicDetailsFragment
} from 'generated/graphql';

import { selectCurrentUser } from 'app/_state/auth/auth-selectors';
import { selectCampaignId } from 'app/_state/router/router-selectors';
import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import { isNilOrEmpty } from 'helpers';
import { ToastrService } from 'core/toastr.service';
import { NOTE_CHANGES } from 'app/note/note.actions';

/**
 * Format of data for the dialog
 */
export interface ReassignFollowUpFlagDialogData {
  campaignId: string;
  entityName: string;
  followUp: FollowUpDetailsFragment;
}

@Component({
  selector: 'app-reassign-follow-up-flag-dialog',
  templateUrl: './reassign-follow-up-flag.dialog.html'
})
export class ReassignFollowUpFlagDialog extends WithDisposableSubject() {
  /* will probably be useful later */
  isSaving = false;
  noteControl = new FormControl<string>({ value: this.data.followUp.note?.text, disabled: false }, { validators: [Validators.required] });
  userSearchControl = new FormControl<UserPublicDetailsFragment>(
    { value: this.data.followUp.assignedUser, disabled: false },
    { validators: [Validators.required] });

  filteredResults$ = this.userSearchControl.valueChanges.pipe(
    debounceTime(500),
    switchMap((value) => {
      if (isNilOrEmpty(value) || typeof (value) !== 'string') {
        return of([]);
      }

      return this._store.select(selectCampaignId).pipe(take(1))
        .pipe(
          switchMap((campaignId) => this._searchUsersGQL.fetch({ keyword: value, campaignId }, {
            fetchPolicy: 'network-only'
          }).pipe(
            map(({ data }) => data.searchUsers.edges.map(n => n.node)),
            map((results) => {
              if (isNilOrEmpty(results)) {
                return ['NO_RESULTS'];
              }
              return results;
            }),
            catchError((_) => of([])),
          ))
        );
    })
  );

  formGroup = new FormGroup({
    noteControl: this.noteControl,
    userSearchControl: this.userSearchControl
  });


  get hasChanges() {
    // const noteChanged = this.noteControl.value !== this.data.followUp.note?.text;
    const currentAssigneeId = this.userSearchControl.value?.id ?? null;
    const originalAssigneeId = this.data.followUp.assignedUser?.id ?? null;
    return currentAssigneeId !== originalAssigneeId;
  }

  static open(dialog: MatDialog, config: MatDialogConfig<ReassignFollowUpFlagDialogData>) {
    return dialog.open(ReassignFollowUpFlagDialog, config);
  }

  constructor(
    private _store: Store,
    public _dialogRef: MatDialogRef<ReassignFollowUpFlagDialog>,
    private _cd: ChangeDetectorRef,
    private _assignFollowUpGQL: AssignFollowUpGQL,
    private _updateNoteGQL: UpdateNoteGQL,
    private _toastrService: ToastrService,
    private _searchUsersGQL: SearchUserQueryGQL,
    @Inject(MAT_DIALOG_DATA) public data: ReassignFollowUpFlagDialogData
  ) {
    super();
  }


  userDisplayFn(user: UserPublicDetailsFragment) {
    if (!user) { return ''; }
    return `${user.firstName} ${user.lastName}`;
  }


  onSubmit() {
    if (this.isSaving) { return; }
    this.isSaving = true;

    this._assignFollowUpGQL.mutate({
      input: {
        followUpId: this.data.followUp.id,
        assignUserId: this.userSearchControl.value.id
      }
    }).pipe(
      finalize(() => {
        this.isSaving = false;
        this._cd.markForCheck();
      })
    ).subscribe({
      next: () => {
        this._toastrService.success('Follow up re-assigned');
        this._dialogRef.close();
      },
      error: (error) => {
        this._toastrService.error(`Failed to re-assign follow-up flag.
        The user you are trying to assign the follow-up to may not have access to this entity.`);
      }
    });

    // const observables = [];


    // observables.push(assignFollowUp$);


    // if (this.data.followUp.note.text !== this.noteControl.value) {
    //   const updateNote$ = this._updateNoteGQL.mutate({
    //     input: {
    //       targetType: NoteTarget.FollowUp,
    //       noteId: this.data.followUp.note.id,
    //       campaignId: this.data.campaignId,
    //       changes: {
    //         text: this.noteControl.value
    //       }
    //     }
    //   }).pipe(
    //     finalize(() => {
    //       this._store.dispatch(NOTE_CHANGES({
    //         payload: {
    //           targetId: this.data.followUp.id,
    //           targetType: NoteTarget.FollowUp,
    //           campaignId: this.data.campaignId
    //         }
    //       }));
    //     })
    //   );
    //   observables.push(updateNote$);
    // }

    // if (observables.length > 0) {
    //   merge(...observables).pipe(
    //     finalize(() => {
    //       this.isSaving = false;
    //       this._toastrService.success('Follow up updated');
    //       this.close();
    //     })
    //   ).subscribe();
    // } else {
    //   this.isSaving = false;
    // }
  }

  close() {
    this._dialogRef.close();
  }

}
